import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { TipoDivergenciaCompraEnum } from '@shared/enums/compras/tipo-divergencia-compra.enum';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';
import type { KanbanCard, Person } from '../../../processo-compras.models';
import { CompraOcorrenciaStruct } from '@shared/services/structs/compras/compra-ocorrencia.struct';

/** Desfecho escolhido no painel; o card-edit-dialog despacha para os fluxos existentes (mover ou cancelar). */
export enum DesfechoDivergenciaEnum {
  Finalizar = 1,
  VoltarConferencia = 2,
  Cancelar = 3,
  AceitarSeguir = 4,
  ReverVinculo = 5,
}

/** Divergência registrada no histórico (ocorrência Divergência registrada), pronta para o cartão. */
interface DivergenciaRegistrada {
  idCompraOcorrencia: number;
  isConferencia: boolean;
  tipo: string;
  texto: string;
  autor: string;
  dataOcorrencia: string;
  referencia: string;
  quantidade: number | null;
}

/** Campos opcionais do `dados` das ocorrências Divergência registrada (vínculo da NF ou conferência física). */
interface DadosDivergencia {
  origem?: string;
  idTipoDivergenciaCompra?: number;
  quantidade?: number | null;
  referencia?: string | null;
  quantidadeRelacionada?: number | null;
  valorUnitarioNota?: number | null;
}

/** Marca gravada pelo Compras no `dados` do problema registrado na conferência física. */
const ORIGEM_CONFERENCIA = 'conferencia';

/**
 * Etapa Divergência (abaixo do bloco da NF): divergências registradas no histórico do pedido e o desfecho por
 * origem — vínculo da NF: aceitar e seguir para Pedido Faturado ou rever o vínculo; conferência: aceitar e
 * finalizar ou voltar para Conferência; ambos: recusar e cancelar. O clique só emite `desfecho`.
 */
@Component({
  selector: 'app-ced-stage-divergencia',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, AlertCardComponent],
  templateUrl: './ced-stage-divergencia.component.html',
  styleUrl: './ced-stage-divergencia.component.css',
})
export class CedStageDivergenciaComponent implements OnChanges {
  @Input({ required: true }) card!: KanbanCard;
  @Input() ocorrencias: CompraOcorrenciaStruct[] = [];
  @Input() usersById?: Map<number, Person>;
  @Input() somenteLeitura = false;
  @Input() transitions: Record<string, string[]> = {};
  @Output() desfecho = new EventEmitter<DesfechoDivergenciaEnum>();

  readonly desfechoEnum = DesfechoDivergenciaEnum;
  divergencias: DivergenciaRegistrada[] = [];
  /** Há divergência apurada no vínculo da NF: desfechos "Aceitar e seguir" e "Rever vínculo". */
  temDivergenciaVinculo = false;
  /** Há problema da conferência física (ou nenhuma divergência registrada): "Aceitar e finalizar" e "Voltar para Conferência". */
  temDivergenciaConferencia = false;

  ngOnChanges(): void {
    this.divergencias = this.ocorrencias
      .filter(e => e.idTipoOcorrenciaCompra === TipoOcorrenciaCompraEnum.DivergenciaRegistrada)
      .sort((a, b) => new Date(b.dataOcorrencia).getTime() - new Date(a.dataOcorrencia).getTime())
      .map(e => {
        let dados: DadosDivergencia = {};
        try {
          const parsed = e.dados ? JSON.parse(e.dados) : null;
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) dados = parsed;
        } catch { /* dados malformado: fica só a descrição da ocorrência */ }

        const isConferencia = dados.origem === ORIGEM_CONFERENCIA;
        let tipo = '';
        if (isConferencia) {
          tipo = dados.idTipoDivergenciaCompra === TipoDivergenciaCompraEnum.Avaria
            ? 'Volume com avaria'
            : 'Divergência nas quantidades';
        } else if (dados.valorUnitarioNota != null) {
          tipo = 'Divergência de preço';
        } else if (dados.quantidadeRelacionada != null) {
          tipo = 'Divergência de quantidade';
        }

        return {
          idCompraOcorrencia: e.idCompraOcorrencia,
          isConferencia,
          tipo,
          texto: e.descricao || e.nomeTipoOcorrencia,
          autor: e.nomeUsuario || this.usersById?.get(e.idUsuario ?? -1)?.name || 'Usuário não identificado',
          dataOcorrencia: e.dataOcorrencia,
          referencia: isConferencia ? (dados.referencia ?? '') : '',
          quantidade: isConferencia && typeof dados.quantidade === 'number' ? dados.quantidade : null,
        };
      });
    this.temDivergenciaVinculo = this.divergencias.some(d => !d.isConferencia);
    // Sem divergência registrada (movimento manual) mantém os desfechos da conferência.
    this.temDivergenciaConferencia = this.divergencias.some(d => d.isConferencia) || !this.temDivergenciaVinculo;
  }

  /** Motivo do bloqueio do desfecho (tooltip); vazio = habilitado. */
  motivoBloqueio(desfecho: DesfechoDivergenciaEnum): string {
    if (this.somenteLeitura) return 'Pedido arquivado ou em etapa final.';
    if (desfecho === DesfechoDivergenciaEnum.ReverVinculo) return '';
    // Cancelar segue o "Cancelar compra" do menu: só com a movimentação Divergência → Cancelado configurada (o backend recusa sem ela).
    if (desfecho === DesfechoDivergenciaEnum.Cancelar) {
      return (this.transitions[this.card.stage] ?? []).includes('cancelado')
        ? ''
        : 'Movimentação Divergência → Cancelado não configurada.';
    }
    if (desfecho === DesfechoDivergenciaEnum.AceitarSeguir) {
      return (this.transitions[this.card.stage] ?? []).includes('faturado')
        ? ''
        : 'Movimentação Divergência → Pedido Faturado não configurada.';
    }
    const destino = desfecho === DesfechoDivergenciaEnum.Finalizar ? 'finalizado' : 'conferencia';
    return (this.transitions[this.card.stage] ?? []).includes(destino)
      ? ''
      : 'Movimentação não configurada para esta etapa.';
  }

  escolher(desfecho: DesfechoDivergenciaEnum): void {
    if (this.motivoBloqueio(desfecho)) return;
    this.desfecho.emit(desfecho);
  }
}
